// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
    ITextFieldStyles,
    TextField,
} from 'office-ui-fabric-react';

import {
    Progress,
    saveZipCodeData,
    updateCreateOrderProgress
} from './../../store/orderCreation';

import { validatePostalCode } from 'src/helpers/validation';
import { selectedCountryInfoSelector } from './../../store/countries';

import Styles from './zipCode.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        marginLeft: 0,
        width: 360,
    },
};

export default function ZipCode() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [zipCode, setZipCode] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');

    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const country = countryInfo && countryInfo.countryCode;

    const buttonDisabled = !zipCode;

    function onZipCodeChange(event: any) {
        setZipCode(event.target.value && event.target.value.trim());
    }

    function onClick() {
        if (!validateZipCode()) {
            setErrorMessage(t('please_provide_valid_postal_code'));
            return;
        }

        logPageAction('Submit valid zip code: Continue button clicked');

        dispatch(
            saveZipCodeData({
                zipCode,
            })
        );
        dispatch(
            updateCreateOrderProgress(Progress.ItemSelection)
        );
    }

    function validateZipCode(): boolean {
        if (!zipCode || !validatePostalCode(country, zipCode)) {
            return false;
        }
        return true;
    }

    const buttonClassName = clsx(
        Styles.button,
        { [Styles.continueDisabled]: buttonDisabled }
    );

    function checkInput(e: any) {
        const re = /[0-9a-zA-Z -]+/g;
        if (!re.test(e.key)) {
            e.preventDefault();
        }
    }

    return (
        <section id="zip_code" className={Styles.zipCodeContainer}>
            <div className={Styles.desc}>
                {t('create_zipcode')}
            </div>
            <div className={Styles.inputContainer}>
                <TextField
                    autoFocus={true}
                    ariaLabel={t('zip_code')}
                    label={t('zip_code')}
                    errorMessage={errorMessage}
                    required={true}
                    onChange={onZipCodeChange}
                    styles={textFieldStyles}
                    onKeyPress={checkInput}
                />
                <button
                    aria-label={t('continue')}
                    className={buttonClassName}
                    onClick={onClick}
                >
                    {t('continue')}
                </button>
            </div>
        </section>
    );
}

